'use client';

import Clarity from '@microsoft/clarity';
import { Container, Typography, FormGroup, FormControlLabel, Switch, Box, Button, Link } from '@mui/material';
import * as React from 'react';

import { CookieIcon } from '../../assets';
import {
  ClarityPaper,
  StyledButton,
  StyledBackdrop,
  HeaderBox,
  CookieIconWrapper,
  ConsentText,
  ConsentDivider,
  ActionsStack
} from './Clarity.styles';

const STORAGE_KEY = 'v5-epos-cookie-consent';
const VERSION = '1.01';

type ConsentKey = 'functional' | 'clarity' | 'marketing';

type ConsentPrefs = {
  version: string;
  choices: Record<ConsentKey, boolean>;
};

const DEFAULT_PREFS: ConsentPrefs = {
  version: VERSION,
  choices: { functional: true, clarity: true, marketing: false }
};

export interface ClarityConsentProps {
  projectId: string;
  privacyLink?: string;
}

// ---- helpers ----
const persist = (next: ConsentPrefs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
};
const clear = () => {
  localStorage.removeItem(STORAGE_KEY);
};
const migration = (saved: ConsentPrefs) => {
  switch (saved.version.toString()) {
    case '1':
      clear();
      return null;
    default:
      clear();
      return null;
  }
};

export default function ClarityConsent({
  projectId,
  privacyLink: privacyHref = 'https://pm.floatingcube.com/w/url/'
}: ClarityConsentProps) {
  const [open, setOpen] = React.useState(false);
  const [prefs, setPrefs] = React.useState<ConsentPrefs>(DEFAULT_PREFS);

  const applyClarityConsent = (allow: boolean) => {
    try {
      Clarity.consent(allow);
    } catch {
      /* empty */
    }
  };

  // ---- on mount ----
  React.useEffect(() => {
    try {
      Clarity.init(projectId);
    } catch {
      /* empty */
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        let parsed: ConsentPrefs | null = JSON.parse(raw) as ConsentPrefs;
        if (parsed && parsed?.version !== VERSION) {
          parsed = migration(parsed);
        }
        if (parsed && typeof parsed === 'object' && parsed.choices && typeof parsed.choices.clarity === 'boolean') {
          setPrefs(parsed);
          applyClarityConsent(parsed.choices.clarity);
          setOpen(false);
          return;
        }
      } catch {
        /* empty */
      }
    }

    applyClarityConsent(false);
    setOpen(true);
  }, [projectId]);

  // ---- actions ----
  const allowAll = () => {
    const next: ConsentPrefs = {
      version: VERSION,
      choices: { functional: true, clarity: true, marketing: true }
    };
    setPrefs(next);
    persist(next);
    applyClarityConsent(true);
    setOpen(false);
  };

  const rejectAll = () => {
    const next: ConsentPrefs = {
      version: VERSION,
      choices: { functional: true, clarity: false, marketing: false }
    };
    setPrefs(next);
    persist(next);
    applyClarityConsent(false);
    setOpen(false);
  };

  const saveChoices = () => {
    const next = prefs;
    setPrefs(next);
    persist(next);
    applyClarityConsent(prefs.choices.clarity);
    setOpen(false);
  };

  const toggle = (key: ConsentKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const choices = { ...prefs.choices, [key]: e.target.checked };
    setPrefs({ ...prefs, choices });
  };

  if (!open) return null;

  return (
    <StyledBackdrop id='clarity-consent-backdrop' open={open}>
      <ClarityPaper elevation={0}>
        <Container disableGutters>
          <Box display='grid' gap={2} width='100%' gridTemplateRows='auto auto auto'>
            {/* Header */}
            <HeaderBox>
              <CookieIconWrapper>
                <CookieIcon />
              </CookieIconWrapper>
              <ConsentText variant='body1'>
                We use cookies to ensure the best experience. Choose what you want to allow.
                <br />
                By clicking <b>Save & close</b>, your choices will be remembered.{' '}
                <Link
                  className='highlight-link underline-link'
                  href={privacyHref}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <span>Learn more</span>
                </Link>
                .
              </ConsentText>
            </HeaderBox>

            {/* Options */}
            <Box>
              <FormGroup>
                <FormControlLabel
                  control={<Switch checked={prefs.choices.functional} onChange={toggle('functional')} disabled />}
                  label={
                    <Box>
                      <Typography variant='subtitle2'>Functional (required)</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Needed for core site functionality. Always on.
                      </Typography>
                    </Box>
                  }
                />
                <ConsentDivider />
                <FormControlLabel
                  control={<Switch checked={prefs.choices.clarity} onChange={toggle('clarity')} />}
                  label={
                    <Box>
                      <Typography variant='subtitle2'>Analytics & Session Recording (Clarity)</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Helps us improve UX via anonymized session insights.
                      </Typography>
                    </Box>
                  }
                />
                <ConsentDivider />
                <FormControlLabel
                  control={<Switch checked={prefs.choices.marketing} onChange={toggle('marketing')} />}
                  label={
                    <Box>
                      <Typography variant='subtitle2'>Marketing</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Personalization and campaign measurement.
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {/* Actions */}
            <ActionsStack>
              <Button variant='text' size='medium' color='neutral' onClick={rejectAll}>
                <span>Reject all</span>
              </Button>
              <Button variant='outlined' size='medium' color='success' onClick={allowAll}>
                <span>Allow all</span>
              </Button>
              <StyledButton variant='contained' size='medium' color='primary' onClick={saveChoices}>
                <span>Save & close</span>
              </StyledButton>
            </ActionsStack>
          </Box>
        </Container>
      </ClarityPaper>
    </StyledBackdrop>
  );
}
